import { isNewRegistrationEnabled } from "./lib/registrationAvailability";
import { lazy, Suspense, useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ActiveLocationProvider } from "./context/ActiveLocationContext";
import { BillingAccessContext, effectiveBillingAccess, useBillingAccess } from "./context/BillingAccessContext";
import { TerminologyProvider } from "./context/TerminologyContext";
import { subscriptionAPI } from "./lib/api";
import { trackPageView } from "./lib/analytics";
import AutomaticUpdates from "./components/AutomaticUpdates";
import RouteLoadBoundary from "./components/RouteLoadBoundary";
import AuthScopeBoundary from "./components/AuthScopeBoundary";

import Landing from "./pages/Landing";
import Aerzte from './pages/Aerzte';
import Login from "./pages/Login";
import Register from "./pages/Register";
const AnnualRegistration = lazy(() => import("./pages/AnnualRegistration"));
import VerifyEmail from "./pages/VerifyEmail";
import SetPassword from "./pages/SetPassword";

import PublicStudio from "./pages/PublicStudio";
import PublicBooking from "./pages/PublicBooking";
import PublicCancel from "./pages/PublicCancel";

import PublicForm from "./pages/PublicForm";
import PublicEstimateAcceptance from "./pages/PublicEstimateAcceptance";
import PublicDataCheck from "./pages/PublicDataCheck";
import PublicFeeAgreement from "./pages/PublicFeeAgreement";
import Impressum from "./pages/Impressum";
import Datenschutz from "./pages/Datenschutz";
import Nutzungsbedingungen from "./pages/Nutzungsbedingungen";
import AccountDeletion from "./pages/AccountDeletion";
import Support from "./pages/Support";
import AndroidDownload from "./pages/AndroidDownload";
import HelpChat from "./components/HelpChat";

import CookieConsentBanner from "./components/CookieConsentBanner";

import DashboardLayout from "./components/DashboardLayout";
import AdminRoute from "./components/AdminRoute";

import AdminLogin from "./pages/admin/AdminLogin";

const Dashboard = lazy(() => import("./pages/Dashboard"));
const Calendar = lazy(() => import("./pages/Calendar"));
const ReadOnlyCalendar = lazy(() => import("./pages/ReadOnlyCalendar"));
const WorkSchedule = lazy(() => import("./pages/WorkSchedule"));
const Services = lazy(() => import("./pages/Services"));
const Materials = lazy(() => import("./pages/Materials"));
const Staff = lazy(() => import("./pages/Staff"));
const Clients = lazy(() => import("./pages/Clients"));
const PatientDataChecks = lazy(() => import("./pages/PatientDataChecks"));
const ClientDetail = lazy(() => import("./pages/ClientDetail"));
const Settings = lazy(() => import("./pages/Settings"));
const ApiDocs = lazy(() => import("./pages/ApiDocs"));
const Reports = lazy(() => import("./pages/Reports"));
const Invoices = lazy(() => import("./pages/Invoices"));
const PatientAcquisition = lazy(() => import("./pages/PatientAcquisition"));
const Upgrade = lazy(() => import("./pages/Upgrade"));
const Policies = lazy(() => import("./pages/Policies"));
const Waitlist = lazy(() => import("./pages/Waitlist"));
const AdminOrgVisit = lazy(() => import("./pages/AdminOrgVisit"));
const Locations = lazy(() => import("./pages/Locations"));
const LocationForm = lazy(() => import("./pages/LocationForm"));
const GiftCards = lazy(() => import("./pages/GiftCards"));
const Marketing = lazy(() => import("./pages/Marketing"));
const NewsletterCampaigns = lazy(() => import("./pages/NewsletterCampaigns"));
const Messenger = lazy(() => import("./pages/Messenger"));
const InstagramAutomations = lazy(() => import("./pages/InstagramAutomations"));
const Shop = lazy(() => import("./pages/Shop"));
const AdManager = lazy(() => import("./pages/AdManager"));
const Forms = lazy(() => import("./pages/Forms"));
const FormSubmissions = lazy(() => import("./pages/FormSubmissions"));
const AdminHelpChats = lazy(() => import("./pages/admin/AdminHelpChats"));
const Setup = lazy(() => import("./pages/Setup"));
const AdminLayout = lazy(() => import("./pages/admin/AdminLayout"));
const AdminOrgs = lazy(() => import("./pages/admin/AdminOrgs"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminPlans = lazy(() => import("./pages/admin/AdminPlans"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));
const AdminPayments = lazy(() => import("./pages/admin/AdminPayments"));
const AdminStripeEvents = lazy(() => import("./pages/admin/AdminStripeEvents"));
const AdminLexoffice = lazy(() => import("./pages/admin/AdminLexoffice"));
const AdminReports = lazy(() => import("./pages/admin/AdminReports"));
const AdminAnalytics = lazy(() => import("./pages/admin/AdminAnalytics"));
const AdminSoftwareLeads = lazy(() => import("./pages/admin/AdminSoftwareLeads"));
const AdminAdTemplates = lazy(() => import("./pages/admin/AdminAdTemplates"));
const AutomationBuilder = lazy(() => import("./pages/AutomationBuilder"));
const AdminSoftwarePartners = lazy(() => import("./pages/admin/AdminSoftwarePartners"));
const AdminAddons = lazy(() => import("./pages/admin/AdminAddons"));

const FullscreenSpinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-white">
    <div className="animate-spin rounded-full h-8 w-8 border-2 border-black border-t-transparent"></div>
  </div>
);

const API_URL = process.env.REACT_APP_BACKEND_URL;

const isLocalHost = (host) => {
  const normalized = String(host || "").toLowerCase();
  return normalized === "localhost" || normalized === "127.0.0.1" || normalized.endsWith(".local");
};

function DomainAwareHome() {
  const [resolvedStudioDomain, setResolvedStudioDomain] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const publishHomeContext = (isPractice) => {
      window.__mibPracticeHome = isPractice;
      window.dispatchEvent(new Event('mib:home-context-resolved'));
    };
    const run = async () => {
      const host = window.location.hostname;
      if (!API_URL || isLocalHost(host)) {
        if (!cancelled) {
          publishHomeContext(false);
          setResolvedStudioDomain(false);
          setChecking(false);
        }
        return;
      }

      try {
        const res = await fetch(`${API_URL}/api/public/studio/__host__`);
        const data = await res.json().catch(() => null);
        const hasStudio = Boolean(res.ok && data.id);
        if (!cancelled) { publishHomeContext(hasStudio); setResolvedStudioDomain(hasStudio); }
      } catch (_err) {
        if (!cancelled) { publishHomeContext(false); setResolvedStudioDomain(false); }
      } finally {
        if (!cancelled) setChecking(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, []);

  if (checking) return <FullscreenSpinner />;
  if (resolvedStudioDomain) return <PublicStudio />;
  return <Landing />;
}

const ProtectedRoute = ({ children }) => (
  <AuthScopeBoundary fallback={<FullscreenSpinner />}><ProtectedWorkspace>{children}</ProtectedWorkspace></AuthScopeBoundary>
);

const ProtectedWorkspace = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();
  const [subscriptionChecked, setSubscriptionChecked] = useState(false);
  const [checkoutRequired, setCheckoutRequired] = useState(false);
  const [billingAccess, setBillingAccess] = useState({ mode: 'normal' });
  const [billingNow, setBillingNow] = useState(Date.now());

  useEffect(() => {
    let cancelled = false;
    if (!isAuthenticated) {
      setCheckoutRequired(false);
      setBillingAccess({ mode: 'normal' });
      setSubscriptionChecked(true);
      return;
    }

    setSubscriptionChecked(false);
    const refresh = () => subscriptionAPI.getFresh()
      .then((res) => {
        if (cancelled) return;
        const sub = res?.data || {};
        setCheckoutRequired(Boolean(sub.checkout_required));
        setBillingAccess({ ...(sub.billing_access || { mode: 'normal' }), payment_review_required: sub.software_payment_review_required === true });
        setBillingNow(Date.now());
      })
      .catch(() => {
        // A temporary network error must not clear a known payment restriction.
      })
      .finally(() => {
        if (!cancelled) setSubscriptionChecked(true);
      });
    void refresh();
    const refreshTimer = window.setInterval(refresh, 120000);
    window.addEventListener('focus', refresh);

    return () => {
      cancelled = true;
      window.clearInterval(refreshTimer);
      window.removeEventListener('focus', refresh);
    };
  }, [isAuthenticated]);

  useEffect(() => {
    if (billingAccess.mode !== 'grace' && billingAccess.scope !== 'software') return undefined;
    const deadline = new Date(billingAccess.scope === 'software' ? billingAccess.valid_until : billingAccess.grace_until || '').getTime();
    if (!Number.isFinite(deadline)) return undefined;
    const timer = window.setTimeout(() => setBillingNow(Date.now()), Math.max(0, deadline - Date.now() + 50));
    return () => window.clearTimeout(timer);
  }, [billingAccess]);

  const currentBillingAccess = effectiveBillingAccess(billingAccess, billingNow);

  if (loading) return <FullscreenSpinner />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!subscriptionChecked) return <FullscreenSpinner />;
  if (currentBillingAccess.mode === 'restricted' && currentBillingAccess.scope !== 'software' && !['/calendar', '/upgrade'].includes(location.pathname)) {
    return <Navigate to="/calendar" replace />;
  }
  if (currentBillingAccess.mode === 'normal' && checkoutRequired && !["/upgrade", "/settings", "/api-docs"].includes(location.pathname)) {
    return <Navigate to="/upgrade?required=1" replace />;
  }
  return (
    <BillingAccessContext.Provider value={currentBillingAccess}>
      <ActiveLocationProvider>
        <TerminologyProvider>{children}</TerminologyProvider>
      </ActiveLocationProvider>
    </BillingAccessContext.Provider>
  );
};

function CalendarRoute() {
  const billingAccess = useBillingAccess();
  return billingAccess.mode === 'restricted' ? <ReadOnlyCalendar /> : <Calendar />;
}

const RoleRoute = ({ roles, permission, children }) => {
  const { user, loading } = useAuth();
  if (loading) return <FullscreenSpinner />;
  const role = String(user?.role || "").toLowerCase();
  const hasRole = roles.includes(role);
  const hasPermission = permission && Array.isArray(user?.permissions) && user.permissions.includes(permission);
  if (!hasRole && !hasPermission) return <Navigate to="/calendar" replace />;
  return children;
};

const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();
  if (!isAuthenticated) return children;
  if (loading) return <FullscreenSpinner />;
  if (isAuthenticated) {
    const params = new URLSearchParams(location.search);
    const next = params.get("next");
    return <Navigate to={next || "/dashboard"} replace />;
  }
  return children;
};

function RegistrationRedirect() {
  const location = useLocation();
  return <Navigate to={`/register${location.search}${location.hash}`} replace />;
}

// Check if this is admin-only deployment
const IS_ADMIN_MODE = process.env.REACT_APP_ADMIN_MODE === 'true';

function AdminOnlyRoutes() {
  return (
    <Routes>
      <Route path="/" element={<AdminLogin />} />
      <Route path="/login" element={<AdminLogin />} />
      <Route path="/set-password" element={<SetPassword />} />

      <Route path="/admin" element={
        <AdminRoute>
          <AdminLayout><AdminOrgs /></AdminLayout>
        </AdminRoute>
      } />
      <Route path="/admin/users" element={
        <AdminRoute>
          <AdminLayout><AdminUsers /></AdminLayout>
        </AdminRoute>
      } />
      <Route path="/admin/plans" element={
        <AdminRoute>
          <AdminLayout><AdminPlans /></AdminLayout>
        </AdminRoute>
      } />
      <Route path="/admin/software-partners" element={
        <AdminRoute><AdminLayout><AdminSoftwarePartners /></AdminLayout></AdminRoute>
      } />
      <Route path="/admin/addons" element={
        <AdminRoute>
          <AdminLayout><AdminAddons /></AdminLayout>
        </AdminRoute>
      } />
      <Route path="/admin/reports" element={
        <AdminRoute>
          <AdminLayout><AdminReports /></AdminLayout>
        </AdminRoute>
      } />
      <Route path="/admin/analytics" element={
        <AdminRoute>
          <AdminLayout><AdminAnalytics /></AdminLayout>
        </AdminRoute>
      } />
      <Route path="/admin/software-leads" element={
        <AdminRoute>
          <AdminLayout><AdminSoftwareLeads /></AdminLayout>
        </AdminRoute>
      } />
      <Route path="/admin/help-chats" element={
        <AdminRoute>
          <AdminLayout><AdminHelpChats /></AdminLayout>
        </AdminRoute>
      } />
      <Route path="/admin/ad-templates" element={
        <AdminRoute>
          <AdminLayout><AdminAdTemplates /></AdminLayout>
        </AdminRoute>
      } />
      <Route path="/admin/payments" element={
        <AdminRoute>
          <AdminLayout><AdminPayments /></AdminLayout>
        </AdminRoute>
      } />
      <Route path="/admin/stripe-events" element={
        <AdminRoute>
          <AdminLayout><AdminStripeEvents /></AdminLayout>
        </AdminRoute>
      } />
      <Route path="/admin/lexoffice" element={
        <AdminRoute>
          <AdminLayout><AdminLexoffice /></AdminLayout>
        </AdminRoute>
      } />
      <Route path="/admin/settings" element={<AdminRoute><AdminLayout><AdminSettings /></AdminLayout></AdminRoute>} />
      <Route path="/admin/leads" element={<Navigate to="/admin" replace />} />

      <Route path="/api-docs" element={
        <AdminRoute>
          <AdminLayout><ApiDocs /></AdminLayout>
        </AdminRoute>
      } />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function LegacyMessengerRedirect() {
  const { search, hash } = useLocation();
  return <Navigate to={`/messenger${search}${hash}`} replace />;
}

function LegacyInquiryRedirect() {
  const { search, hash } = useLocation();
  return <Navigate to={`/anfragen${search}${hash}`} replace />;
}

function UserRoutes() {
  return (
    <Routes>
      <Route path="/" element={<DomainAwareHome />} />
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/register" element={isNewRegistrationEnabled() ? <Suspense fallback={<FullscreenSpinner />}><AnnualRegistration /></Suspense> : <PublicRoute><Register /></PublicRoute>} />
      <Route path="/register/annual" element={<RegistrationRedirect />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/set-password" element={<SetPassword />} />
      <Route path="/studio/:slug" element={<PublicStudio />} />
      <Route path="/book/:orgSlug" element={<PublicBooking />} />
      <Route path="/cancel/:appointmentId" element={<PublicCancel />} />
      <Route path="/form/:token" element={<PublicForm />} />
      <Route path="/angebot-annehmen" element={<PublicEstimateAcceptance />} />
      <Route path="/datencheck/:token" element={<PublicDataCheck />} />
      <Route path="/f/:slugCode" element={<PublicForm />} />
      <Route path="/hv/:code" element={<PublicFeeAgreement />} />
      <Route path="/admin/visit" element={<AdminOrgVisit />} />
      {/* Landingpage fuer die Aerzte-Akquise. Eigene Subdomain, eigenes
          Design: Sie verkauft die Leistung, nicht die Software dahinter. */}
      <Route path="/aerzte" element={<Aerzte />} />
      <Route path="/impressum" element={<Impressum />} />
      <Route path="/datenschutz" element={<Datenschutz />} />
      <Route path="/nutzungsbedingungen" element={<Nutzungsbedingungen />} />
      <Route path="/konto-loeschen" element={<AccountDeletion />} />
      <Route path="/support" element={<Support />} />
      <Route path="/android-download" element={<AndroidDownload />} />

      <Route element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
        <Route path="/dashboard" element={
          <RoleRoute roles={["owner", "admin"]} permission="dashboard_access">
            <Dashboard />
          </RoleRoute>
        } />
        <Route path="/calendar" element={<CalendarRoute />} />
        <Route path="/work-schedule" element={<WorkSchedule />} />
        <Route path="/services" element={<Services />} />
        <Route path="/materials" element={<Materials />} />
        <Route path="/staff" element={<Staff />} />
        <Route path="/clients" element={<Clients />} />
        <Route path="/clients/data-checks" element={<PatientDataChecks />} />
        <Route path="/clients/new" element={<ClientDetail />} />
        <Route path="/clients/:clientId" element={<ClientDetail />} />
        <Route path="/waitlist" element={<Waitlist />} />
        <Route path="/vouchers" element={<GiftCards />} />
        <Route path="/marketing/newsletter" element={<Marketing />} />
        <Route path="/marketing/newsletter/versendet" element={<NewsletterCampaigns />} />
        <Route path="/marketing/newsletter/versendet/:campaignId" element={<NewsletterCampaigns />} />
        <Route path="/marketing/automations" element={<AutomationBuilder />} />
        <Route path="/marketing/automations/instagram" element={<InstagramAutomations />} />
        <Route path="/marketing/messenger" element={<LegacyMessengerRedirect />} />
        <Route path="/messenger" element={<Messenger />} />
        <Route path="/shop" element={<Shop />} />
        <Route path="/forms" element={<FormSubmissions />} />
        <Route path="/forms/verwalten" element={<Forms />} />
        <Route path="/content-bibliothek" element={<AdManager />} />
        <Route path="/anfragen" element={
          <RoleRoute roles={[]} permission="patient_acquisition:read">
            <PatientAcquisition />
          </RoleRoute>
        } />
        {/* Alte Route bleibt dauerhaft bestehen: gespeicherte Lesezeichen und
            geteilte gefilterte Ansichten duerfen nicht ins Leere laufen. */}
        <Route path="/patientenfuehrung" element={<LegacyInquiryRedirect />} />
        <Route path="/patientengewinnung" element={<LegacyInquiryRedirect />} />
        <Route path="/reports" element={
          <RoleRoute roles={["owner", "admin"]} permission="reports_access">
            <Reports />
          </RoleRoute>
        } />
        <Route path="/invoices/*" element={
          <RoleRoute roles={["owner", "admin"]} permission="reports_access">
            <Invoices />
          </RoleRoute>
        } />
        <Route path="/upgrade" element={
          <RoleRoute roles={["owner", "admin"]} permission="billing:manage">
            <Upgrade />
          </RoleRoute>
        } />
        <Route path="/settings" element={
          <RoleRoute roles={["owner", "admin"]} permission="settings:manage">
            <Settings />
          </RoleRoute>
        } />
        <Route path="/api-docs" element={<ApiDocs />} />
        <Route path="/policies" element={<Policies />} />
        <Route path="/locations" element={<Locations />} />
        <Route path="/locations/new" element={<LocationForm />} />
        <Route path="/locations/:locationId" element={<LocationForm />} />
        <Route path="/setup" element={<Setup />} />
      </Route>
      <Route path="/ad-manager" element={<Navigate to="/content-bibliothek" replace />} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function AppRoutes() {
  return IS_ADMIN_MODE ? <AdminOnlyRoutes /> : <UserRoutes />;
}

function ConditionalHelpChat() {
  const location = useLocation();
  const isPublicPage =
    location.pathname === "/angebot-annehmen" ||
    location.pathname === "/" ||
    location.pathname === "/login" ||
    location.pathname.startsWith("/register") ||
    location.pathname.startsWith("/book/") ||
    location.pathname.startsWith("/studio/") ||
    location.pathname.startsWith("/form/") ||
    location.pathname.startsWith("/f/") ||
    ["/impressum", "/datenschutz", "/nutzungsbedingungen", "/konto-loeschen", "/support", "/android-download"].includes(location.pathname);
  if (isPublicPage) return null;
  return <HelpChat />;
}

function AnalyticsTracker() {
  const location = useLocation();

  useEffect(() => {
    if (location.pathname === "/register" || location.pathname === "/register/annual" || location.pathname === "/angebot-annehmen" || /^\/(book|studio|form|f)\//.test(location.pathname)) return undefined;
    const handleConsent = () => {
      if (location.pathname === '/' && window.__mibPracticeHome !== false) return;
      trackPageView();
    };
    const timer = window.setTimeout(handleConsent, 0);
    window.addEventListener("mib:cookie-consent-updated", handleConsent);
    window.addEventListener('mib:home-context-resolved', handleConsent);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("mib:cookie-consent-updated", handleConsent);
      window.removeEventListener('mib:home-context-resolved', handleConsent);
    };
  }, [location.pathname, location.search]);

  return null;
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <RouteLoadBoundary><Suspense fallback={<FullscreenSpinner />}><AppRoutes /></Suspense></RouteLoadBoundary>
        <AnalyticsTracker />
        <ConditionalHelpChat />
        <CookieConsentBanner />
        <AutomaticUpdates />
        <Toaster position="bottom-left" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}
